/* eslint-disable */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Volume2, Wifi, Smartphone, KeyRound, ArrowRight, RotateCcw, AlertTriangle, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { playBell, unlockAudio } from "../lib/audio";
import { getBranding, subscribeBranding } from "../lib/branding";
import { getDeviceId, getDeviceLabel, setDeviceLabel } from "../lib/device";

const STEPS = [
  {
    id: "api",
    label: "Server verbinding",
    icon: Wifi,
    description: "Controleert of de festival-server bereikbaar is",
  },
  {
    id: "audio",
    label: "Geluid",
    icon: Volume2,
    description: "Speelt een testtoon — tik de speaker voor je telefoon",
  },
  {
    id: "sumup",
    label: "SumUp betaling",
    icon: Smartphone,
    description: "Controleert of SumUp App-switch geconfigureerd is",
  },
  {
    id: "offline",
    label: "Offline opslag",
    icon: Wifi,
    description: "Test of orders bewaard kunnen worden zonder internet",
  },
  {
    id: "slot",
    label: "Slot claim",
    icon: KeyRound,
    description: "Probeert een test-slot te reserveren en weer vrij te geven",
  },
];

export default function Setup() {
  const [brand, setBrand] = useState(getBranding());
  const [label, setLabelState] = useState(getDeviceLabel());
  const [results, setResults] = useState({}); // {stepId: "pass"|"fail"|"running"|"pending"}
  const [running, setRunning] = useState(false);
  const [audioPrompt, setAudioPrompt] = useState(false);
  const navigate = useNavigate();

  useEffect(() => subscribeBranding(setBrand), []);

  const reset = () => {
    setResults({});
    setAudioPrompt(false);
  };

  const setStep = (id, status, detail) =>
    setResults((r) => ({ ...r, [id]: { status, detail } }));

  const runAll = async () => {
    if (running) return;
    setRunning(true);
    reset();

    // 1. API
    setStep("api", "running");
    try {
      const { data } = await api.get("/");
      if (data?.ok) {
        setStep("api", "pass", `${data.app} bereikbaar`);
      } else {
        setStep("api", "fail", "Onverwacht antwoord");
      }
    } catch (e) {
      setStep("api", "fail", "Geen verbinding");
      setRunning(false);
      return;
    }

    // 2. Audio
    setStep("audio", "running");
    unlockAudio();
    try {
      playBell();
    } catch {}
    setAudioPrompt(true);

    // De rest gaat door, audio wacht op user-bevestiging
    await runRemaining();
  };

  const runRemaining = async () => {
    // 3. SumUp config
    setStep("sumup", "running");
    try {
      const { data } = await api.get("/payments/sumup/config");
      if (data?.appswitch_available) {
        setStep("sumup", "pass", "App-switch actief");
      } else {
        setStep("sumup", "fail", "Niet geconfigureerd — alleen contant of Stripe mogelijk");
      }
    } catch {
      setStep("sumup", "fail", "Status niet ophaalbaar");
    }

    // 4. Offline opslag (localStorage)
    setStep("offline", "running");
    try {
      const probeKey = "setup-probe";
      const probeVal = String(Date.now());
      localStorage.setItem(probeKey, probeVal);
      const read = localStorage.getItem(probeKey);
      localStorage.removeItem(probeKey);
      if (read === probeVal) {
        setStep("offline", "pass", "localStorage werkt — offline queue beschikbaar");
      } else {
        setStep("offline", "fail", "localStorage onbereikbaar");
      }
    } catch {
      setStep("offline", "fail", "Browser blokkeert opslag");
    }

    // 5. Slot claim → release roundtrip op een SETUP-TEST resource
    setStep("slot", "running");
    const deviceId = getDeviceId();
    const testResource = `setup_probe_${deviceId.slice(0, 8)}`;
    try {
      await api.post("/locks/claim", {
        resource_type: "slot",
        resource_id: testResource,
        device_id: deviceId,
        label: `Setup test · ${getDeviceLabel() || "onbekend"}`,
      });
      // direct weer vrijgeven
      await api.post("/locks/release", {
        resource_type: "slot",
        resource_id: testResource,
        device_id: deviceId,
      });
      setStep("slot", "pass", "Slot-systeem werkt");
    } catch (e) {
      setStep("slot", "fail", e?.response?.data?.detail || "Lock test mislukt");
    }

    setRunning(false);
  };

  const confirmAudio = (heard) => {
    setAudioPrompt(false);
    if (heard) {
      setStep("audio", "pass", "Geluid bevestigd");
    } else {
      setStep(
        "audio",
        "fail",
        "Geen geluid — zet apparaat op luid of test de speaker"
      );
    }
  };

  const allDone = STEPS.every((s) => results[s.id]?.status === "pass" || results[s.id]?.status === "fail");
  const allPass = STEPS.every((s) => results[s.id]?.status === "pass");
  const anyFail = STEPS.some((s) => results[s.id]?.status === "fail");

  return (
    <div className="min-h-screen bg-[var(--bg-soft)] flex flex-col" data-testid="setup-screen">
      <div
        className="px-5 pt-5 pb-3"
        style={{ borderBottom: `4px solid ${brand.hoofdkleur}` }}
      >
        <button
          onClick={() => navigate("/")}
          className="btn-press inline-flex items-center gap-1 text-xs font-bold text-[var(--ink-soft)] mb-2"
          data-testid="setup-back-btn"
        >
          <ArrowLeft className="w-3 h-3" /> Terug naar inloggen
        </button>
        <div
          className="text-xs uppercase tracking-[0.3em] font-bold"
          style={{ color: brand.hoofdkleur }}
        >
          Device-check
        </div>
        <div className="font-display text-2xl font-extrabold" data-testid="setup-title">
          {brand.bedrijfsnaam || "QDB AI SYSTEEM"}
        </div>
        <p className="text-xs text-[var(--ink-soft)] mt-1 leading-relaxed max-w-sm">
          Run deze test één keer per telefoon. Resultaten verschijnen hieronder.
          Pas inloggen na <strong>5 ✓ groen</strong>.
        </p>
      </div>

      <div className="px-5 py-4 flex-1">
        <div className="tile p-4 mb-4">
          <div className="text-xs uppercase tracking-widest text-[var(--ink-soft)] font-bold mb-2">
            Apparaat-label (zichtbaar voor admin)
          </div>
          <input
            value={label}
            onChange={(e) => {
              setLabelState(e.target.value);
              setDeviceLabel(e.target.value);
            }}
            placeholder="Bv. Kassa-telefoon 1"
            className="input"
            data-testid="setup-label"
          />
          <div className="text-[10px] text-[var(--ink-soft)] mt-2 font-mono">
            device-id: {getDeviceId().slice(0, 18)}…
          </div>
        </div>

        <div className="space-y-2" data-testid="setup-steps">
          {STEPS.map((step, i) => {
            const res = results[step.id];
            const Icon = step.icon;
            const status = res?.status || "pending";
            return (
              <div
                key={step.id}
                className="tile p-4 flex items-start gap-3"
                data-testid={`setup-step-${step.id}`}
              >
                <div
                  className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background:
                      status === "pass"
                        ? "var(--success)"
                        : status === "fail"
                        ? "var(--danger)"
                        : status === "running"
                        ? brand.hoofdkleur
                        : "var(--neutral)",
                    color:
                      status === "pending" ? "var(--ink-soft)" : "#fff",
                  }}
                >
                  {status === "running" ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : status === "pass" ? (
                    <CheckCircle2 className="w-5 h-5" />
                  ) : status === "fail" ? (
                    <XCircle className="w-5 h-5" />
                  ) : (
                    <Icon className="w-5 h-5" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-widest text-[var(--ink-soft)] font-bold">
                      Stap {i + 1}
                    </span>
                    {status === "pass" && (
                      <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--success)]" data-testid={`setup-pass-${step.id}`}>
                        ✓ OK
                      </span>
                    )}
                    {status === "fail" && (
                      <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--danger)]" data-testid={`setup-fail-${step.id}`}>
                        ✗ NIET OK
                      </span>
                    )}
                  </div>
                  <div className="font-display font-bold text-lg">{step.label}</div>
                  <div className="text-xs text-[var(--ink-soft)]">
                    {res?.detail || step.description}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Audio bevestigings-prompt */}
        {audioPrompt && (
          <div className="tile p-4 mt-4 border-2" style={{ borderColor: brand.hoofdkleur }} data-testid="audio-prompt">
            <div className="flex items-start gap-3">
              <div
                className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 text-white"
                style={{ background: brand.hoofdkleur }}
              >
                <Volume2 className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="font-bold mb-2">Hoorde je de bel?</div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => confirmAudio(true)}
                    className="btn-press px-3 py-3 rounded-2xl bg-[var(--success)] text-white font-bold"
                    data-testid="audio-yes"
                  >
                    ✓ Ja
                  </button>
                  <button
                    onClick={() => confirmAudio(false)}
                    className="btn-press px-3 py-3 rounded-2xl bg-[var(--danger)] text-white font-bold"
                    data-testid="audio-no"
                  >
                    ✗ Nee
                  </button>
                </div>
                <button
                  onClick={() => {
                    unlockAudio();
                    try { playBell(); } catch {}
                  }}
                  className="btn-press w-full mt-2 px-3 py-2 rounded-xl bg-white border-2 border-[var(--neutral)] text-sm font-bold"
                  data-testid="audio-replay"
                >
                  Speel opnieuw af
                </button>
              </div>
            </div>
          </div>
        )}

        {allDone && (
          <div
            className="tile p-5 mt-4 text-center"
            style={{
              background: allPass ? "rgba(14,143,42,0.08)" : "rgba(214,40,40,0.06)",
              border: `2px solid ${allPass ? "var(--success)" : "var(--danger)"}`,
            }}
            data-testid="setup-result-banner"
          >
            {allPass ? (
              <>
                <CheckCircle2 className="w-10 h-10 mx-auto text-[var(--success)] mb-2" />
                <div className="font-display text-xl font-extrabold text-[var(--success)]">
                  Klaar voor het festival!
                </div>
                <div className="text-xs text-[var(--ink-soft)] mt-1">
                  Alle 5 checks groen. Tik hieronder om in te loggen.
                </div>
              </>
            ) : (
              <>
                <AlertTriangle className="w-10 h-10 mx-auto text-[var(--danger)] mb-2" />
                <div className="font-display text-xl font-extrabold text-[var(--danger)]">
                  {anyFail ? "Een of meer checks faalden" : "Test nog niet compleet"}
                </div>
                <div className="text-xs text-[var(--ink-soft)] mt-1">
                  Toon dit scherm aan een admin en los de gefaalde stap op.
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div className="sticky bottom-0 left-0 right-0 bg-white border-t-2 border-[#ecebe6] p-3 flex gap-2" style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.75rem)" }}>
        {!allDone && (
          <button
            onClick={runAll}
            disabled={running}
            className="btn-press flex-1 inline-flex items-center justify-center gap-2 px-4 py-4 rounded-2xl text-white font-display font-extrabold text-lg disabled:opacity-50"
            style={{ background: brand.hoofdkleur }}
            data-testid="setup-start-btn"
          >
            {running ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
            {running ? "Bezig…" : "Start check"}
          </button>
        )}
        {allDone && (
          <>
            <button
              onClick={reset}
              className="btn-press inline-flex items-center justify-center gap-2 px-4 py-4 rounded-2xl bg-white border-2 border-[var(--neutral)] font-bold"
              data-testid="setup-reset-btn"
            >
              <RotateCcw className="w-4 h-4" /> Opnieuw
            </button>
            <button
              onClick={() => navigate("/")}
              className="btn-press flex-1 inline-flex items-center justify-center gap-2 px-4 py-4 rounded-2xl text-white font-display font-extrabold text-lg"
              style={{ background: allPass ? "var(--success)" : "var(--ink)" }}
              data-testid="setup-continue-btn"
            >
              Naar inloggen <ArrowRight className="w-5 h-5" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
